/**
 * **A burning ship's fire**, as ENHANCED EFFECTS' particle emitter sees her.
 *
 * The third tenant of the trick AD-018 worked out for a burning building and
 * AD-014c reused for a wreck on the ground, and it is reused here rather than
 * rebuilt for the reason that argument keeps proving: a `BurningAircraft` record
 * is a position, a pair of axes, a velocity and two intensities, and none of
 * those four is a fact about aeroplanes.
 *
 * ## Why the fire is particles and the smoke is not
 *
 * Her plume is `ship-smoke.ts` — a palette ribbon read off her pose, drawn
 * whatever the toggles say, because a coal-burning warship that makes no smoke
 * is the percept this lane was opened on. Her **fire** is here, on the particle
 * path, because a flame is exactly what that path is for and there is no
 * palette-ribbon way to draw one that would not be a worse `EXPLO.BMP`.
 *
 * The division has a consequence worth stating: with ENHANCED EFFECTS **off**,
 * a burning ship makes heavy black smoke and no visible flame. That is the
 * toggle's own replace-not-add rule landing where it always does — the original
 * draws no fire on anything that is not an aeroplane — and the smoke still tells
 * the pilot what he has done.
 *
 * `plumeIntensity` is therefore deliberately **low**. The ribbon is already
 * drawing her smoke; what the particle path adds is the flame at the foot of it,
 * and a second full-rate column on top of the first is the fog bank AD-018
 * measured and tuned its way out of.
 */

import {
  SHIP_BEAM_M,
  shipMotionAt,
  shipSinkElapsedS,
  shipStageOf,
  SHIP_SINK_SETTLE_PHASE,
  SHIP_SINK_SECONDS,
} from "@red-opus/engine";
import type { BurningAircraft, FireRunner } from "./particle-sources.ts";
import { SHIP_WATERLINE_Y_M } from "../three/ship-mounts.ts";

/**
 * Metres above her waterline the flame is anchored, in her own body frame.
 *
 * Five, which is the top of her boiler casing (`ship-design.md` §3.2: 4.70) plus
 * a little — a warship's fire is in her superstructure and her boiler room
 * uptakes, not on her forecastle. `particle-effects.ts` throws its flame two
 * metres along the source's own forward axis, and this hands it a forward axis
 * of **straight down** (`collectSceneryFires`'s trick), so the flame band lands
 * on the deck itself and the smoke is born above it.
 */
const SHIP_FLAME_HEIGHT_M = 5 + 2;

/**
 * How wide the fire is. **Her beam**, 10.21 m — a ship burns across her width
 * and along one part of her length, and using her half-*length* the way a
 * building uses its half-extent would put flame from her stem to her stern.
 */
const SHIP_FLAME_SPREAD_M = SHIP_BEAM_M;

/** The particle plume's rate for a burning ship. Low: `ship-smoke.ts`'s ribbon is already drawing her smoke. */
export const SHIP_PLUME_INTENSITY = 0.25;

/**
 * Fraction of the sink sequence her flame survives.
 *
 * The same fraction her weather deck goes under at (`SHIP_SINK_SETTLE_PHASE`),
 * which is not a coincidence dressed up as a rule: fire needs something above
 * water to burn, and past that point she has nothing. From there she is smoke
 * and steam until she is gone.
 */
const FLAME_UNTIL = SHIP_SINK_SETTLE_PHASE;

/**
 * Fills `into` from `offset` with every ship that is alight, and returns the new
 * total — the same records `collectSceneryFires` and `collectCrashFires` write,
 * for the same zero-allocation reason.
 *
 * Only `afire` and `sinking` ships are collected. A ship merely *smoking* has
 * holes in her and no fire, and drawing a flame for one would tell the pilot he
 * had lit something he had not — `collectSceneryFires`'s own sentence about a
 * barn with four holes in it.
 */
export function collectShipFires(runner: FireRunner, into: BurningAircraft[], offset: number): number {
  let count = offset;
  const state = runner.encounter();
  // `?? []` is not defensive programming against the engine — `EncounterState`
  // always has both fields. It is the contract `FireRunner` states, and which
  // `collectSceneryFires` next door spells out at its own `?? []`: these
  // collectors take a slice precisely so a test can hand them a hand-made
  // roster, and several do, built before either field existed.
  const targets = state.targets ?? [];
  for (const ship of state.ships ?? []) {
    // A linear scan rather than a map: `SHIP_MAX_COUNT` is three, and building
    // an index would be the one allocation a frame this pool does not make.
    const hull = targets.find((target) => target.id === ship.id);
    if (!hull) continue;
    const stage = shipStageOf(hull);
    if (stage !== "afire" && stage !== "sinking") continue;
    const slot = into[count];
    if (!slot) break;
    const elapsed = shipSinkElapsedS(hull);
    const motion = shipMotionAt(ship.patrol, state.timeS, elapsed);
    slot.id = `ship-fire-${ship.id}`;
    slot.x = motion.x;
    // Her marks, less how far she has settled, plus the height of her uptakes.
    // The renderer's waterline rather than the sim's zero, because this is a
    // picture and the sea is where the renderer drew it.
    slot.y = SHIP_WATERLINE_Y_M - motion.sinkM + SHIP_FLAME_HEIGHT_M;
    slot.z = motion.z;
    // Forward straight down and up up: see `SHIP_FLAME_HEIGHT_M`.
    slot.forwardX = 0;
    slot.forwardY = -1;
    slot.forwardZ = 0;
    slot.upX = 0;
    slot.upY = 1;
    slot.upZ = 0;
    // Unlike a building she travels, so the flame streams aft with her — and
    // stops streaming on its own as her way comes off, because `speedMps` is
    // already going to zero.
    slot.vx = Math.sin(motion.headingRad) * motion.speedMps;
    slot.vy = 0;
    slot.vz = -Math.cos(motion.headingRad) * motion.speedMps;
    slot.damageFraction = 1;
    slot.burning = stage === "afire" || elapsed < FLAME_UNTIL * SHIP_SINK_SECONDS;
    // Nothing on the spark path knows about a ship: her hits are counted on her
    // hull's own `hitsTaken`, which is not the aeroplane-to-aeroplane counter
    // `particle-effects.ts` differences.
    slot.gunHitsTaken = 0;
    slot.plumeIntensity = SHIP_PLUME_INTENSITY;
    slot.spreadScale = SHIP_FLAME_SPREAD_M;
    count += 1;
  }
  return count;
}
